// The sections of a child's Blueprint as forms
// (02-DESIGN/13-blueprints/01-the-blueprint.md § the console: Workspaces):
// one per section of the document, each drawn in the design's words — the
// sign-in from the same fields the workspace's own sign-in takes; required
// settings as the fields they fix, for a required workload, *every model*,
// *every toolkit* or a solution; guardrails in the Guardrails form; the
// workloads the workspace must run, started from one of this workspace's own
// models or toolkits where that helps; solutions from the catalogue; secrets
// sealed here; limits as numbers. Each edits its part of the draft
// (forms.ts); nothing here speaks to the service but the reads that fill
// the pick lists, and the page saves the whole document once.

import { blueprint } from "@impire-io/sdk";
import { useState, type ReactNode } from "react";

import { Button, Choices, Field, Notice, Pill, Select, TextInput, bytes } from "../../ui/components.tsx";
import type { Entry as CatalogueEntry, Setting } from "../../wire/solutions.ts";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { RuleForm } from "../guardrails/RuleForm.tsx";
import { BLANK, STARTERS } from "../guardrails/starters.ts";
import { guards } from "../guardrails/words.ts";
import { draftOf as ruleDraftOf } from "../guardrails/RuleForm.tsx";
import { ProviderFields } from "../settings/ProviderFields.tsx";
import { RulesEditor } from "../workloads/editors.tsx";
import { GRANT_CAPABILITIES, reenter, SECTIONS, secretKey, type Attested } from "./blueprint.ts";
import {
  blankWorkload,
  dropSolution,
  dropWorkload,
  fieldHints,
  IMAGE,
  LIMIT_FIELDS,
  renameHandle,
  ruleError,
  slotOfSecret,
  targetOf,
  targetWords,
  TARGET_WORDS,
  validHandle,
  type Draft,
  type FieldHint,
  type FieldRow,
  type FormSection,
  type RuleDraft,
  type WorkloadDraft,
  type WorkloadKind,
} from "./forms.ts";

type SetDraft = (next: Draft) => void;

function section(key: FormSection) {
  return SECTIONS.find((s) => s.key === key);
}

/**
 * SectionCard is one section of the blueprint, the content of its tab
 * (01-the-console.md § the blueprint's page): its line, what the form can
 * see is wrong, the form. The tab names the section, so the card does not.
 */
function SectionCard({
  name,
  error,
  aside,
  line,
  children,
}: {
  name: FormSection;
  error: string | undefined;
  aside?: string;
  /** What the section says in place of the design's line for it. */
  line?: string;
  children: ReactNode;
}) {
  const sec = section(name);
  return (
    <div className="card section stack">
      <div>
        <p className="small muted">{line ?? sec?.line}</p>
        {aside !== undefined && <p className="small faint">{aside}</p>}
      </div>
      {error !== undefined && <p className="small bad">{error}</p>}
      {children}
    </div>
  );
}

// ——— the fields of a settings document ———

/**
 * FieldsEditor is a settings document as its top-level fields: each a name,
 * offered from the fields the target's reader names, and a value — text as
 * itself, anything else as JSON. A field present is required whole.
 */
export function FieldsEditor({
  rows,
  onChange,
  hints,
  listId,
}: {
  rows: FieldRow[];
  onChange: (rows: FieldRow[]) => void;
  hints: readonly FieldHint[];
  listId: string;
}) {
  return (
    <div className="pairs">
      {hints.length > 0 && (
        <datalist id={listId}>
          {hints.map((h) => (
            <option key={h.field} value={h.field}>
              {h.what}
            </option>
          ))}
        </datalist>
      )}
      {rows.map((r, i) => {
        const hint = hints.find((h) => h.field === r.field.trim());
        return (
          <div className="row" key={i}>
            <input
              className="input mono"
              style={{ flex: 1, minWidth: "7rem" }}
              placeholder="field"
              aria-label="Field"
              list={hints.length > 0 ? listId : undefined}
              value={r.field}
              title={hint?.what}
              onChange={(e) => {
                onChange(rows.map((q, j) => (j === i ? { ...q, field: e.target.value } : q)));
              }}
              autoComplete="off"
              spellCheck={false}
            />
            <span className="faint">=</span>
            <input
              className="input mono"
              style={{ flex: 2, minWidth: "8rem" }}
              placeholder={hint?.what ?? "value"}
              aria-label="Value"
              value={r.value}
              onChange={(e) => {
                onChange(rows.map((q, j) => (j === i ? { ...q, value: e.target.value } : q)));
              }}
              autoComplete="off"
              spellCheck={false}
            />
            <Button
              kind="quiet"
              size="sm"
              onClick={() => {
                onChange(rows.filter((_, j) => j !== i));
              }}
            >
              Remove
            </Button>
          </div>
        );
      })}
      <div className="row">
        <Button
          size="sm"
          onClick={() => {
            onChange([...rows, { field: "", value: "" }]);
          }}
        >
          Add a field
        </Button>
        <span className="small faint">
          A value is text, or JSON for a number, a list or a set of fields: 0.2, [&quot;chat&quot;], {"{"}
          &quot;temperature&quot;: 0.2{"}"}.
        </span>
      </div>
    </div>
  );
}

// ——— sign-in ———

export function SignInSection({
  draft,
  setDraft,
  error,
  child,
}: {
  draft: Draft;
  setDraft: SetDraft;
  error: string | undefined;
  child: string;
}) {
  const si = draft.signIn;
  const set = (patch: Partial<typeof si>) => {
    setDraft({ ...draft, signIn: { ...si, ...patch } });
  };
  return (
    <SectionCard name="sign-in" error={si.on ? error : undefined}>
      <Choices
        name="sign-in"
        value={si.on ? "on" : "off"}
        onChange={(v) => {
          set({ on: v === "on" });
        }}
        options={[
          {
            value: "off",
            label: `${child} signs in as it chooses`,
            hint: "Its admins set up its sign-in; you require nothing of it.",
          },
          {
            value: "on",
            label: "With a sign-in you set",
            hint: `Pushed down as ${child}'s managed provider: its admins read it and cannot change it. Only a provider whose browser exchanges its own code (Entra, Okta) can be; a client secret is never pushed down.`,
          },
        ]}
      />
      {si.on && (
        <>
          {si.rest.exchange === "server-side" && (
            <Notice kind="bad" title="This provider cannot be pushed down">
              Its code is exchanged by Impire with a client secret, and a blueprint carries no secret.
            </Notice>
          )}
          <Field label="Name on the sign-in button" hint="What people know the provider by; never matched.">
            <TextInput
              value={si.displayName}
              onChange={(displayName) => {
                set({ displayName });
              }}
              placeholder="Microsoft Entra"
            />
          </Field>
          <ProviderFields
            checked="The workspace checks it against the provider when its steward applies it; one that fails is reported as not applied."
            d={si.provider}
            set={(patch) => {
              set({ provider: { ...si.provider, ...patch } });
            }}
          />
          <div className="field">
            <span className="label">Who is let in</span>
            <span className="hint" style={{ marginTop: 0, marginBottom: 6 }}>
              Rules matching a claim of the token to a role. {child}&apos;s admins may add rules of their own
              under this provider and never remove yours.
            </span>
            <div className="pairs">
              {si.rows.map((r, i) => (
                <div className="row" key={i}>
                  <input
                    className="input mono"
                    style={{ flex: 1, minWidth: "6rem" }}
                    placeholder="roles"
                    aria-label="Claim"
                    value={r.claim}
                    onChange={(e) => {
                      set({ rows: si.rows.map((q, j) => (j === i ? { ...q, claim: e.target.value } : q)) });
                    }}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <span className="faint">=</span>
                  <input
                    className="input mono"
                    style={{ flex: 1.4, minWidth: "8rem" }}
                    placeholder="Sales.Admin"
                    aria-label="Value"
                    value={r.value}
                    onChange={(e) => {
                      set({ rows: si.rows.map((q, j) => (j === i ? { ...q, value: e.target.value } : q)) });
                    }}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <span className="faint">→</span>
                  <Select
                    value={r.role}
                    onChange={(role) => {
                      set({ rows: si.rows.map((q, j) => (j === i ? { ...q, role } : q)) });
                    }}
                    options={[
                      { value: "admin", label: "admin" },
                      { value: "member", label: "member" },
                    ]}
                  />
                  <Button
                    kind="quiet"
                    size="sm"
                    onClick={() => {
                      set({ rows: si.rows.filter((_, j) => j !== i) });
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <div className="row">
                <Button
                  size="sm"
                  onClick={() => {
                    set({ rows: [...si.rows, { claim: "", value: "", role: "member" }] });
                  }}
                >
                  Add a rule
                </Button>
                {si.rows.length === 0 && (
                  <span className="small faint">
                    With none, nobody is let in under it until {child}&apos;s admins add rules.
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="field">
            <span className="label">A sign-in of its own</span>
            <Choices
              name="own-provider"
              value={si.ownProvider}
              onChange={(ownProvider) => {
                set({ ownProvider });
              }}
              options={[
                {
                  value: "allowed",
                  label: "Allowed beside yours",
                  hint: `${child}'s admins may also set one up, within its plan.`,
                },
                {
                  value: "refused",
                  label: "Only yours",
                  hint: "One that exists is removed, and none can be set up while this stands.",
                },
              ]}
            />
          </div>
        </>
      )}
    </SectionCard>
  );
}

// ——— required settings ———

export function RequiredSection({
  draft,
  setDraft,
  error,
  manifests,
}: {
  draft: Draft;
  setDraft: SetDraft;
  error: string | undefined;
  manifests: ReadonlyMap<string, readonly Setting[]>;
}) {
  const targets: { value: string; label: string }[] = [
    { value: IMAGE.model, label: `${TARGET_WORDS[IMAGE.model] ?? ""}, whoever added it` },
    { value: IMAGE.toolkit, label: `${TARGET_WORDS[IMAGE.toolkit] ?? ""}, whoever added it` },
    ...draft.workloads
      .filter((w) => w.handle.trim() !== "")
      .map((w) => ({
        value: w.handle.trim(),
        label: `the required workload ${targetWords(w.handle.trim(), draft)}`,
      })),
    ...draft.solutions.map((s) => ({ value: s.name, label: `the solution ${s.name}` })),
  ];
  const setAt = (i: number, patch: Partial<Draft["required"][number]>) => {
    setDraft({ ...draft, required: draft.required.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  };
  return (
    <SectionCard name="required" error={error}>
      {draft.required.length === 0 && <p className="faint">No setting is required.</p>}
      {draft.required.map((r, i) => {
        const known = targetOf(r.target, draft) !== undefined;
        const options = known
          ? targets
          : [{ value: r.target, label: `${r.target} (not in this blueprint)` }, ...targets];
        const hints = fieldHints(r.target, draft, manifests);
        return (
          <div className="card stack" style={{ padding: 16 }} key={i}>
            <div className="row" style={{ alignItems: "flex-end", gap: 8 }}>
              <div className="grow">
                <Field
                  label="For"
                  hint={
                    targetOf(r.target, draft) === "image"
                      ? "Every workload of the workspace running this image — the ones its admins added and the ones they add later — reads these fields and cannot change them."
                      : "What reads these fields, and cannot change them."
                  }
                >
                  <Select
                    value={r.target}
                    onChange={(target) => {
                      setAt(i, { target });
                    }}
                    options={options}
                  />
                </Field>
              </div>
              <Button
                kind="quiet"
                size="sm"
                onClick={() => {
                  setDraft({ ...draft, required: draft.required.filter((_, j) => j !== i) });
                }}
              >
                Remove
              </Button>
            </div>
            <FieldsEditor
              rows={r.fields}
              onChange={(fields) => {
                setAt(i, { fields });
              }}
              hints={hints}
              listId={`required-fields-${String(i)}`}
            />
            {hints.length > 0 && (
              <details>
                <summary className="small">What {targetWords(r.target, draft)} reads</summary>
                <dl className="fields">
                  {hints.map((h) => (
                    <div key={h.field} style={{ display: "contents" }}>
                      <dt>{h.field}</dt>
                      <dd>{h.what}</dd>
                    </div>
                  ))}
                </dl>
                <span className="hint">
                  A hint, not a check: what reads the fields validates them itself.
                </span>
              </details>
            )}
          </div>
        );
      })}
      <div className="row">
        <Button
          size="sm"
          onClick={() => {
            const used = new Set(draft.required.map((r) => r.target));
            const free = targets.find((t) => !used.has(t.value));
            setDraft({
              ...draft,
              required: [
                ...draft.required,
                { target: free?.value ?? IMAGE.model, fields: [{ field: "", value: "" }] },
              ],
            });
          }}
        >
          Add a requirement
        </Button>
      </div>
    </SectionCard>
  );
}

// ——— guardrails ———

/** RULE names the guardrail form in the history entry's state: `rule:new`, or `rule:<index>` for a change. */
const RULE = "rule:";

/** ruleIndexOf reads which rule a form names: -1 for a new one, undefined for none or one the draft does not hold. */
export function ruleIndexOf(open: string | undefined, count: number): number | undefined {
  if (open?.startsWith(RULE) !== true) {
    return undefined;
  }
  const rest = open.slice(RULE.length);
  if (rest === "new") {
    return -1;
  }
  const i = /^[0-9]+$/.test(rest) ? Number(rest) : NaN;
  return Number.isInteger(i) && i < count ? i : undefined;
}

export function RulesSection({
  draft,
  setDraft,
  error,
  child,
}: {
  draft: Draft;
  setDraft: SetDraft;
  error: string | undefined;
  child: string;
}) {
  // The guardrail's form stands in the tab's place (01-the-console.md § conventions: a form).
  const form = useFormState();
  const index = ruleIndexOf(form.open, draft.rules.length);
  if (index !== undefined) {
    return (
      <RuleFormPage
        child={child}
        initial={index >= 0 ? draft.rules[index] : undefined}
        onClose={form.close}
        onDone={(r) => {
          const rules = index >= 0 ? draft.rules.map((x, j) => (j === index ? r : x)) : [...draft.rules, r];
          setDraft({ ...draft, rules });
          form.close();
        }}
      />
    );
  }
  return (
    <SectionCard name="rules" error={error}>
      {draft.rules.length === 0 ? (
        <p className="faint">No guardrail is required.</p>
      ) : (
        <div className="list">
          {draft.rules.map((r, i) => (
            <div className="li" key={i}>
              <span className="grow">
                <span className="cell-main mono">{r.draft.id || "unnamed"}</span>
                <span className="cell-sub">
                  {r.draft.selector.trim() === ""
                    ? "guards nothing yet"
                    : `guards ${guards(r.draft.selector.trim())}`}
                </span>
              </span>
              {ruleError(r.draft) !== undefined && <Pill tone="bad">incomplete</Pill>}
              <Pill tone={r.draft.enabled ? "live" : "neutral"}>{r.draft.enabled ? "on" : "off"}</Pill>
              <Button
                kind="quiet"
                size="sm"
                onClick={() => {
                  form.show(`${RULE}${String(i)}`);
                }}
              >
                Change
              </Button>
              <Button
                kind="quiet"
                size="sm"
                onClick={() => {
                  setDraft({ ...draft, rules: draft.rules.filter((_, j) => j !== i) });
                }}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="row">
        <Button
          size="sm"
          onClick={() => {
            form.show(`${RULE}new`);
          }}
        >
          Add a guardrail
        </Button>
      </div>
    </SectionCard>
  );
}

/** RuleFormPage is the Guardrails form in the tab's place, started from a worked rule or nothing, kept in the blueprint on *Done*. */
function RuleFormPage({
  child,
  initial,
  onClose,
  onDone,
}: {
  child: string;
  initial: RuleDraft | undefined;
  onClose: () => void;
  onDone: (r: RuleDraft) => void;
}) {
  const [from, setFrom] = useState("blank");
  const [rule, setRule] = useState<RuleDraft>(initial ?? { draft: ruleDraftOf(BLANK), rest: {} });
  const ready = ruleError(rule.draft) === undefined;
  const done = () => {
    if (ready) {
      onDone(rule);
    }
  };
  return (
    <FormPage
      heading="h2"
      title={initial === undefined ? "Require a guardrail" : `Change ${initial.draft.id}`}
      line={`Checked in ${child}'s workloads beside its own guardrails; its admins may add rules and never switch this one off, change it or remove it. Kept here until the blueprint is saved.`}
      back="Guardrails"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={done} disabled={!ready}>
            Done
          </Button>
        </>
      }
    >
      <p className="small muted">
        Someone who must confirm is one of {child}&apos;s people: you approve nothing there.
      </p>
      {initial === undefined && (
        <div className="field">
          <span className="label">Start from</span>
          <Choices
            name="from"
            value={from}
            onChange={(key) => {
              setFrom(key);
              const starter = STARTERS.find((st) => st.key === key);
              setRule({ draft: ruleDraftOf(starter === undefined ? BLANK : starter.rule), rest: {} });
            }}
            options={[
              ...STARTERS.map((st) => ({ value: st.key, label: st.title, hint: st.sentence })),
              { value: "blank", label: "Nothing", hint: "An empty guardrail that lets everything through." },
            ]}
          />
        </div>
      )}
      <RuleForm
        draft={rule.draft}
        onChange={(d) => {
          setRule({ ...rule, draft: d });
        }}
        refused={{}}
        editing={false}
        onSubmit={done}
      />
    </FormPage>
  );
}

// ——— workloads ———

/** OwnWorkload is one of this workspace's own models or toolkits, offered as a start for a required one. */
export interface OwnWorkload {
  id: string;
  name: string;
  kind: "model" | "toolkit";
}

const KIND_WORDS: Record<WorkloadKind, string> = { model: "model", toolkit: "toolkit", image: "image" };

export function WorkloadsSection({
  draft,
  setDraft,
  error,
  saved,
  own,
  ownName,
  onCopy,
}: {
  draft: Draft;
  setDraft: SetDraft;
  error: string | undefined;
  /** The handles the saved blueprint already holds: those are fixed, because the steward finds the workload by them. */
  saved: ReadonlySet<string>;
  own: readonly OwnWorkload[];
  ownName: string;
  onCopy: (w: OwnWorkload) => void;
}) {
  const [copy, setCopy] = useState("");
  const taken = draft.workloads.map((w) => w.handle);
  const setAt = (i: number, patch: Partial<WorkloadDraft>) => {
    setDraft({ ...draft, workloads: draft.workloads.map((w, j) => (j === i ? { ...w, ...patch } : w)) });
  };
  const add = (kind: WorkloadKind) => {
    setDraft({ ...draft, workloads: [...draft.workloads, blankWorkload(kind, taken)] });
  };
  return (
    <SectionCard name="workloads" error={error}>
      {draft.workloads.length === 0 && <p className="faint">No workload is required.</p>}
      {draft.workloads.map((w, i) => {
        const fixed = saved.has(w.handle);
        return (
          <div className="card stack" style={{ padding: 16 }} key={i}>
            <div className="section-head" style={{ marginBottom: 0 }}>
              <h3>
                {w.name.trim() !== "" ? w.name : `A new ${KIND_WORDS[w.kind]}`}{" "}
                <span className="small faint mono">{w.handle}</span>
              </h3>
              <span className="row" style={{ gap: 8 }}>
                <Pill>{w.kind === "image" ? "your image" : `a ${KIND_WORDS[w.kind]}`}</Pill>
                <Button
                  kind="quiet"
                  size="sm"
                  onClick={() => {
                    setDraft(dropWorkload(draft, w.handle));
                  }}
                >
                  Remove
                </Button>
              </span>
            </div>
            <Field label="What it runs">
              <Select
                value={w.kind}
                onChange={(kind) => {
                  setAt(i, { kind });
                }}
                options={[
                  { value: "model", label: "A model: the install's inference image" },
                  { value: "toolkit", label: "A toolkit: the install's MCP runtime" },
                  { value: "image", label: "An image you bring" },
                ]}
              />
            </Field>
            {w.kind === "image" && (
              <Field label="Image" hint="An OCI reference on the install's registry list.">
                <TextInput
                  value={w.image}
                  onChange={(image) => {
                    setAt(i, { image });
                  }}
                  placeholder="registry.example/acme/router:1.4"
                  mono
                />
              </Field>
            )}
            <div className="two">
              <Field label="Name" hint="What people read on its page, beside required by you.">
                <TextInput
                  value={w.name}
                  onChange={(name) => {
                    setAt(i, { name });
                  }}
                  placeholder={w.kind === "model" ? "Fast" : w.kind === "toolkit" ? "GitHub" : "Router"}
                />
              </Field>
              <Field
                label="Handle"
                hint={
                  fixed
                    ? "Fixed: the steward finds it again by this."
                    : validHandle(w.handle)
                      ? "What its required settings and secrets name it by; fixed once saved."
                      : "One word of letters, digits, - and _."
                }
              >
                <TextInput
                  value={w.handle}
                  onChange={(handle) => {
                    setDraft(renameHandle(draft, w.handle, handle));
                  }}
                  mono
                  disabled={fixed}
                />
              </Field>
            </div>
            <Field label="Description">
              <TextInput
                value={w.description}
                onChange={(description) => {
                  setAt(i, { description });
                }}
              />
            </Field>
            <Field
              label="Answers to"
              hint={
                w.kind === "model"
                  ? "The names apps ask for — fast, embed — separated by commas; refused where the workspace already uses one."
                  : "The names it answers discovery under, separated by commas."
              }
            >
              <TextInput
                value={w.names}
                onChange={(names) => {
                  setAt(i, { names });
                }}
                placeholder={w.kind === "model" ? "fast" : ""}
                mono
              />
            </Field>
            <div className="two">
              <Field
                label="Virtual CPUs"
                hint={w.kind === "model" ? "1 when empty." : "The image's default when empty."}
              >
                <TextInput
                  value={w.cpus}
                  onChange={(cpus) => {
                    setAt(i, { cpus });
                  }}
                  type="number"
                  mono
                />
              </Field>
              <Field
                label="Memory (MiB)"
                hint={w.kind === "model" ? "512 when empty." : "The image's default when empty."}
              >
                <TextInput
                  value={w.memory}
                  onChange={(memory) => {
                    setAt(i, { memory });
                  }}
                  type="number"
                  mono
                />
              </Field>
            </div>
            <Field label="Copies" hint="How many run at once; one when empty.">
              <TextInput
                value={w.replicas}
                onChange={(replicas) => {
                  setAt(i, { replicas });
                }}
                type="number"
                mono
              />
            </Field>
            <div className="field">
              <span className="label">What it may reach</span>
              <RulesEditor
                rules={w.egress}
                onChange={(egress) => {
                  setAt(i, { egress });
                }}
              />
              <span className="hint">
                {w.kind === "model"
                  ? "The provider's host when empty."
                  : "Nothing beyond the platform when empty."}
              </span>
            </div>
          </div>
        );
      })}
      <div className="row" style={{ gap: 8 }}>
        <Button
          size="sm"
          onClick={() => {
            add("model");
          }}
        >
          Add a model
        </Button>
        <Button
          size="sm"
          onClick={() => {
            add("toolkit");
          }}
        >
          Add a toolkit
        </Button>
        <Button
          size="sm"
          onClick={() => {
            add("image");
          }}
        >
          Add an image
        </Button>
        {own.length > 0 && (
          <span className="row" style={{ gap: 8 }}>
            <span className="small muted">or copy one of {ownName}&apos;s</span>
            <Select
              value={copy}
              onChange={setCopy}
              options={[
                { value: "", label: "pick a model or toolkit" },
                ...own.map((o) => ({ value: o.id, label: `${o.name} (${o.kind})` })),
              ]}
            />
            <Button
              size="sm"
              disabled={copy === ""}
              onClick={() => {
                const o = own.find((x) => x.id === copy);
                if (o !== undefined) {
                  onCopy(o);
                  setCopy("");
                }
              }}
            >
              Copy
            </Button>
          </span>
        )}
      </div>
      {own.length > 0 && (
        <p className="small faint">
          A copy takes its image, names, resources and reach, and requires its settings of the workspace; its
          key is never copied: seal one in Secrets.
        </p>
      )}
    </SectionCard>
  );
}

// ——— solutions ———

export function SolutionsSection({
  draft,
  setDraft,
  error,
  catalogue,
  catalogueNote,
}: {
  draft: Draft;
  setDraft: SetDraft;
  error: string | undefined;
  catalogue: readonly CatalogueEntry[];
  /** Why the catalogue cannot be read, when it cannot. */
  catalogueNote: string | undefined;
}) {
  const [pick, setPick] = useState("");
  const named = new Set(draft.solutions.map((s) => s.name));
  const available = catalogue.filter((e) => e.withdrawn !== true && !named.has(e.latest.name));
  const entry = (name: string) => catalogue.find((e) => e.latest.name === name);
  const setAt = (i: number, patch: Partial<Draft["solutions"][number]>) => {
    setDraft({ ...draft, solutions: draft.solutions.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  };
  return (
    <SectionCard name="solutions" error={error}>
      {draft.solutions.length === 0 && <p className="faint">No solution is installed by you.</p>}
      {draft.solutions.map((s, i) => {
        const e = entry(s.name);
        const m = e?.latest.manifest;
        const hints = (m?.settings ?? []).filter((x) => x.scope === "tenant" && x.secret !== true);
        return (
          <div className="card stack" style={{ padding: 16 }} key={s.name}>
            <div className="section-head" style={{ marginBottom: 0 }}>
              <h3>
                {m?.display ?? s.name} <span className="small faint mono">{s.name}</span>
              </h3>
              <Button
                kind="quiet"
                size="sm"
                onClick={() => {
                  setDraft(dropSolution(draft, s.name));
                }}
              >
                Remove
              </Button>
            </div>
            {m?.description !== undefined && <p className="small muted">{m.description}</p>}
            {e === undefined && (
              <p className="small bad">Not in the catalogue you can see: the steward reports it unapplied.</p>
            )}
            <Field
              label="Version"
              hint={`The version or channel you pin, where the solution offers one; the latest when empty${m?.version !== undefined ? ` (now ${m.version})` : ""}.`}
            >
              <TextInput
                value={s.version}
                onChange={(version) => {
                  setAt(i, { version });
                }}
                placeholder="latest"
                mono
              />
            </Field>
            <div className="field">
              <span className="label">Settings you fix</span>
              <FieldsEditor
                rows={s.fields}
                onChange={(fields) => {
                  setAt(i, { fields });
                }}
                hints={hints.map((h) => ({ field: h.name, what: h.hint ?? h.label }))}
                listId={`solution-fields-${s.name}`}
              />
              <span className="hint">
                The workspace&apos;s admins set what you leave out and never what you fix. Its secrets are
                sealed in Secrets.
              </span>
            </div>
          </div>
        );
      })}
      {catalogueNote !== undefined ? (
        <p className="small muted">{catalogueNote}</p>
      ) : available.length === 0 ? (
        <p className="small faint">Nothing else in the catalogue to install.</p>
      ) : (
        <div className="row" style={{ gap: 8 }}>
          <Select
            value={pick}
            onChange={setPick}
            options={[
              { value: "", label: "pick a solution from the catalogue" },
              ...available.map((e) => ({
                value: e.latest.name,
                label: e.latest.manifest.display || e.latest.name,
              })),
            ]}
          />
          <Button
            size="sm"
            disabled={pick === ""}
            onClick={() => {
              setDraft({
                ...draft,
                solutions: [...draft.solutions, { name: pick, version: "", fields: [], rest: {} }],
              });
              setPick("");
            }}
          >
            Install there
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

// ——— secrets ———

/** SecretsSection lists the blueprint's secrets, never their values, and seals a new one toward the workspace's vault. */
export function SecretsSection({
  draft,
  setDraft,
  error,
  attested,
}: {
  draft: Draft;
  setDraft: SetDraft;
  error: string | undefined;
  attested: Attested;
}) {
  const key = secretKey(attested);
  const owners = [
    ...draft.workloads.map((w) => ({
      value: w.handle,
      label: w.name.trim() !== "" ? `${w.name} (${w.handle})` : w.handle,
    })),
    ...draft.solutions.map((s) => ({ value: s.name, label: `the solution ${s.name}` })),
  ];
  const [owner, setOwner] = useState(owners[0]?.value ?? "");
  const [name, setName] = useState("api-key");
  const [value, setValue] = useState("");
  const secrets = draft.secrets;
  const target = owners.some((o) => o.value === owner) ? owner : (owners[0]?.value ?? "");
  const add = () => {
    if (key === undefined || target === "" || name === "" || value === "") {
      return;
    }
    const prev = secrets.find((x) => x.for === target && x.name === name);
    const sealed = blueprint.sealSecret(value, key);
    const next: blueprint.Secret = { for: target, name, ...sealed, version: (prev?.version ?? 0) + 1 };
    setDraft({ ...draft, secrets: [...secrets.filter((x) => x !== prev), next] });
    setValue("");
  };
  return (
    <SectionCard name="secrets" error={error}>
      {secrets.length === 0 ? (
        <p className="faint">No secret is delivered.</p>
      ) : (
        <div className="list">
          {secrets.map((x) => (
            <div className="li" key={`${x.for}.${x.name}`}>
              <span className="grow">
                <span className="cell-main mono">
                  {x.for}.{x.name}
                </span>
                <span className="cell-sub">version {x.version} · sealed, never shown again</span>
              </span>
              {reenter(attested, x.for, x.name) && <Pill tone="bad">re-enter</Pill>}
              <Button
                kind="quiet"
                size="sm"
                onClick={() => {
                  setDraft({ ...draft, secrets: secrets.filter((y) => y !== x) });
                }}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
      {key === undefined ? (
        <p className="small muted">
          A secret is sealed toward the key the workspace&apos;s last report vouched. There is none to seal
          toward now: enter secrets once its steward has reported.
        </p>
      ) : owners.length === 0 ? (
        <p className="small muted">A secret belongs to a required workload or a solution: add one first.</p>
      ) : (
        <div className="row" style={{ gap: 8, alignItems: "flex-end" }}>
          <Field label="For">
            <Select value={target} onChange={setOwner} options={owners} />
          </Field>
          <Field label="Name" hint="api-key for a model's provider key; credential for a toolkit's.">
            <TextInput value={name} onChange={setName} mono />
          </Field>
          <Field label="Value" hint="Sealed here; never shown again.">
            <TextInput value={value} onChange={setValue} type="password" />
          </Field>
          <Button onClick={add} disabled={value === "" || name === "" || target === ""}>
            {secrets.some((x) => x.for === target && x.name === name) ? "Seal again" : "Seal"}
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

/**
 * SlotsSection is a named blueprint's secrets (01-the-blueprint.md § named
 * blueprints): what each is for and its name, and no value — each workspace
 * that uses the blueprint enters its own on its page, sealed toward its own
 * vault.
 */
export function SlotsSection({
  draft,
  setDraft,
  error,
}: {
  draft: Draft;
  setDraft: SetDraft;
  error: string | undefined;
}) {
  const owners = [
    ...draft.workloads.map((w) => ({
      value: w.handle,
      label: w.name.trim() !== "" ? `${w.name} (${w.handle})` : w.handle,
    })),
    ...draft.solutions.map((s) => ({ value: s.name, label: `the solution ${s.name}` })),
  ];
  const [owner, setOwner] = useState(owners[0]?.value ?? "");
  const [name, setName] = useState("api-key");
  const slots = draft.secrets;
  const target = owners.some((o) => o.value === owner) ? owner : (owners[0]?.value ?? "");
  const taken = slots.some((x) => x.for === target && x.name === name);
  const add = () => {
    if (target === "" || name === "" || taken) {
      return;
    }
    setDraft({ ...draft, secrets: [...slots, slotOfSecret(target, name)] });
  };
  return (
    <SectionCard
      name="secrets"
      error={error}
      line="The secrets its workloads and solutions need. Each workspace that uses this blueprint enters its own values on its page, sealed toward its own vault."
    >
      {slots.length === 0 ? (
        <p className="faint">No secret is needed.</p>
      ) : (
        <div className="list">
          {slots.map((x) => (
            <div className="li" key={`${x.for}.${x.name}`}>
              <span className="grow">
                <span className="cell-main mono">
                  {x.for}.{x.name}
                </span>
                <span className="cell-sub">each workspace enters its own</span>
              </span>
              <Button
                kind="quiet"
                size="sm"
                onClick={() => {
                  setDraft({ ...draft, secrets: slots.filter((y) => y !== x) });
                }}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
      {owners.length === 0 ? (
        <p className="small muted">A secret belongs to a required workload or a solution: add one first.</p>
      ) : (
        <div className="row" style={{ gap: 8, alignItems: "flex-end" }}>
          <Field label="For">
            <Select value={target} onChange={setOwner} options={owners} />
          </Field>
          <Field label="Name" hint="api-key for a model's provider key; credential for a toolkit's.">
            <TextInput value={name} onChange={setName} mono />
          </Field>
          <Button onClick={add} disabled={name === "" || target === "" || taken}>
            Add
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

// ——— limits ———

export function LimitsSection({
  draft,
  setDraft,
  error,
  catalogue,
}: {
  draft: Draft;
  setDraft: SetDraft;
  error: string | undefined;
  catalogue: readonly CatalogueEntry[];
}) {
  const l = draft.limits;
  const set = (patch: Partial<typeof l>) => {
    setDraft({ ...draft, limits: { ...l, ...patch } });
  };
  const names = [...new Set([...catalogue.map((e) => e.latest.name), ...(l.solutions ?? [])])].sort();
  return (
    <SectionCard name="limits" error={error} aside="within the plan; empty is the plan's bound">
      <div className="two">
        {LIMIT_FIELDS.map((f) => (
          <Field
            key={f.key}
            label={f.label}
            hint={
              f.key === "storage" && l.storage.trim() !== "" && Number(l.storage) > 0
                ? bytes(Number(l.storage))
                : "-1 for no bound."
            }
          >
            <TextInput
              value={l[f.key]}
              onChange={(v) => {
                set({ [f.key]: v });
              }}
              type="number"
              mono
            />
          </Field>
        ))}
        <Field label="May create workspaces of its own">
          <Select
            value={l.manages}
            onChange={(manages) => {
              set({ manages });
            }}
            options={[
              { value: "", label: "as its plan says" },
              { value: "yes", label: "yes" },
              { value: "no", label: "no" },
            ]}
          />
        </Field>
      </div>
      <div className="field">
        <span className="label">Solutions it may install</span>
        <Choices
          name="limit-solutions"
          value={l.solutions === undefined ? "any" : "only"}
          onChange={(v) => {
            set({ solutions: v === "any" ? undefined : (l.solutions ?? []) });
          }}
          options={[
            { value: "any", label: "Any it can see" },
            { value: "only", label: "Only these" },
          ]}
        />
        {l.solutions !== undefined && (
          <span className="row" style={{ marginTop: 8 }}>
            {names.length === 0 && (
              <span className="small faint">The catalogue you can see is empty: none.</span>
            )}
            {names.map((n) => (
              <label className="checkbox" key={n}>
                <input
                  type="checkbox"
                  checked={l.solutions?.includes(n) ?? false}
                  onChange={(e) => {
                    const cur = l.solutions ?? [];
                    set({ solutions: e.target.checked ? [...cur, n] : cur.filter((x) => x !== n) });
                  }}
                />
                <span className="mono">{n}</span>
              </label>
            ))}
          </span>
        )}
      </div>
      <p className="small faint">
        None of these is enforced here but the workspaces it may create: its steward reports one exceeded, and
        you act.
      </p>
      <GrantsEditor draft={draft} setDraft={setDraft} catalogue={catalogue} />
    </SectionCard>
  );
}

/**
 * GrantsEditor is the blueprint's grant lines, on the Limits tab
 * (01-the-console.md § the blueprint's page; 12-workspace-hierarchy/02-grants.md):
 * each a capability and the workspace that receives it — a control grant
 * naming its solution too — kept in the draft as the document carries them.
 */
function GrantsEditor({
  draft,
  setDraft,
  catalogue,
}: {
  draft: Draft;
  setDraft: SetDraft;
  catalogue: readonly CatalogueEntry[];
}) {
  const raw = draft.rest.grants;
  const grants: blueprint.Grant[] = Array.isArray(raw) ? (raw as blueprint.Grant[]) : [];
  const put = (next: blueprint.Grant[]) => {
    const rest = { ...draft.rest };
    if (next.length === 0) {
      delete rest.grants;
    } else {
      rest.grants = next;
    }
    setDraft({ ...draft, rest });
  };
  const set = (i: number, patch: Partial<blueprint.Grant>) => {
    put(
      grants.map((g, j) => {
        if (j !== i) {
          return g;
        }
        const n: blueprint.Grant = { ...g, ...patch };
        if (n.capability !== "control") {
          delete n.solution;
        }
        return n;
      }),
    );
  };
  const solutions = [
    ...new Set([...draft.solutions.map((x) => x.name), ...catalogue.map((e) => e.latest.name)]),
  ].sort();
  return (
    <div className="stack">
      <div>
        <h3>Grants</h3>
        <p className="small muted">
          Records a workspace using this blueprint sends to another workspace, or the one that may instruct
          it. The receiving workspace runs what subscribes, or nothing arrives.
        </p>
      </div>
      {grants.length === 0 && <p className="faint">No grant: nothing leaves the workspace.</p>}
      {grants.map((g, i) => (
        <div className="row" key={i} style={{ alignItems: "flex-end", gap: 8 }}>
          <Field label="What">
            <Select
              value={g.capability}
              onChange={(capability) => {
                set(i, { capability });
              }}
              options={GRANT_CAPABILITIES.map((c) => ({ value: c.value, label: c.label }))}
            />
          </Field>
          <Field label="Goes to" hint="the receiving workspace's address">
            <TextInput
              value={g.receiver}
              onChange={(receiver) => {
                set(i, { receiver });
              }}
              placeholder="acme-security"
              mono
            />
          </Field>
          {g.capability === "control" && (
            <Field label="Solution">
              <Select
                value={g.solution ?? ""}
                onChange={(solution) => {
                  set(i, { solution });
                }}
                options={[
                  { value: "", label: "pick one" },
                  ...solutions.map((n) => ({ value: n, label: n })),
                ]}
              />
            </Field>
          )}
          <Button
            kind="quiet"
            onClick={() => {
              put(grants.filter((_, j) => j !== i));
            }}
          >
            Remove
          </Button>
        </div>
      ))}
      <div>
        <Button
          onClick={() => {
            put([...grants, { capability: "audit", receiver: "" }]);
          }}
        >
          Add a grant
        </Button>
      </div>
    </div>
  );
}
